import { useEffect, useState } from 'react';

import { getErrorMessage } from '../utils/errorHandling';
import { captureAndReportError } from '../utils/errorReporting';
import {
  fetchMetadataJson,
  isMetadataUnavailableError,
} from '../utils/metadataFetch';

interface MetadataFetchResult {
  data: unknown | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

// Cache for metadata to avoid repeated fetches
const metadataCache = new Map<string, unknown>();

export function useMetadataFetch(
  uri: string | null | undefined,
): MetadataFetchResult {
  const [data, setData] = useState<unknown | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchMetadata = async () => {
    if (!uri) {
      setData(null);
      setError(null);
      return;
    }

    const cached = metadataCache.get(uri);
    if (cached) {
      setData(cached);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const metadata = await fetchMetadataJson(uri);

      metadataCache.set(uri, metadata);

      setData(metadata);
      setError(null);
    } catch (err) {
      if (isMetadataUnavailableError(err)) {
        console.warn('Metadata is not available', {
          metadataUri: uri,
          error: err,
        });
      } else {
        captureAndReportError(err, {
          operation: 'Error fetching metadata',
          metadataUri: uri,
        });
      }
      setError(getErrorMessage(err, 'Failed to fetch metadata'));
      setData(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMetadata();
  }, [uri]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchMetadata,
  };
}
